import { currentBusinessPeriod, formatDateOnly } from "../../../utils/datetime";
/**
 * Pure utility functions for AdminUebersichtPage (Step 7.5 Phase 1).
 * NO React, NO hooks, NO JSX, NO constants.
 */

export function roundCurrency(value) {
  return Math.round(Number(value || 0));
}

export function formatCurrency(value) {
  return `CHF ${roundCurrency(value).toLocaleString("de-CH")}`;
}

export function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

/** Maps critical-units API reason.type to German label (KPI C.6). */
export function mapCriticalReasonTypeToLabel(type) {
  const t = String(type || "");
  if (t === "low_occupancy") return "Niedrige Belegung";
  if (t === "low_margin") return "Niedrige Marge";
  if (t === "upcoming_termination") return "Kündigung (ohne Anschluss)";
  return t || "Kriterium";
}

/**
 * Fallback: total_room_days aus period.days_in_month × summary_cards.total_rooms
 * (zwei Backend-Felder, kein KPI-Eigenwert).
 */
export function deriveTotalRoomDaysFromKpis(kpisJson) {
  const d = kpisJson?.period?.days_in_month;
  const n = kpisJson?.summary_cards?.total_rooms;
  if (d == null || n == null) return null;
  return Number(d) * Number(n);
}

/** Nenner für Leerstandstage (vacant / total_room_days): Backend-Feld bevorzugen. */
export function resolveTotalRoomDaysDenominator(kpisJson) {
  if (!kpisJson) return null;
  const fromCard = kpisJson.summary_cards?.vacant_days_this_month?.total_room_days;
  if (typeof fromCard === "number" && !Number.isNaN(fromCard)) return fromCard;
  const fromVacancy = kpisJson.vacancy?.total_room_days;
  if (typeof fromVacancy === "number" && !Number.isNaN(fromVacancy)) return fromVacancy;
  return deriveTotalRoomDaysFromKpis(kpisJson);
}

/** True when `unit` has a non-empty id suitable for `/admin/units/:id`. */
export function hasValidAdminUnitRouteId(unit) {
  if (!unit) return false;
  const raw = unit.id ?? unit.unitId;
  const s = raw != null ? String(raw).trim() : "";
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

/** Last `n` calendar months from today (oldest first). */
export function lastNMonths(n) {
  const out = [];
  const d = new Date();
  for (let i = n - 1; i >= 0; i -= 1) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({ year: dt.getFullYear(), month: dt.getMonth() + 1 });
  }
  return out;
}

/**
 * 6A.3b: map a preset key to a period {mode, from, to} (from/to = {year,month}|null).
 * Pure + deterministic — everything derives from one resolved `{year, month}`, so the
 * picker presets and their tests share one source of truth. (lastNMonths reads
 * new Date() internally and is NOT reused here, to keep this injectable.)
 *
 * P0 (Regel 51): the anchor month is the SWISS business month, resolved by
 * `currentBusinessPeriod`, not `new Date().getFullYear()/.getMonth()`. The page's
 * DEFAULT period already came from `currentBusinessPeriod` (AdminUebersichtPage),
 * while every preset read the DEVICE clock — so around a month boundary a viewer
 * outside Europe/Zurich got a default and a "Aktueller Monat" preset that named two
 * different months. Both halves now come from ONE zone conversion.
 *
 * `now` stays injectable for determinism in tests; it is an INSTANT (Date | ms |
 * ISO string), and it is converted to Swiss wall-clock parts rather than read in
 * the host's zone. Omitting it reads the clock once.
 */
/**
 * The preset keys `PeriodPicker` offers, and the only values that count as a
 * PRESET IDENTITY anywhere downstream (P4).
 *
 * Exported so the picker, the URL parser and the Performance comparison all
 * decide "is this a real preset?" from ONE list. A second copy would drift, and
 * the failure would be silent in the direction that matters: a key this list
 * rejects and another accepts is a period that serialises to a URL nothing can
 * read back.
 */
export const PERIOD_PRESET_KEYS = [
  "current",
  "prev",
  "last3",
  "last6",
  "last12",
  "ytd",
  "prevYear",
  "all",
];

/** Whether `key` is one of the presets above. Strict: no coercion, no aliases. */
export function isPeriodPresetKey(key) {
  return PERIOD_PRESET_KEYS.includes(key);
}

export function presetToPeriod(key, now) {
  // P4: the bounds below are unchanged; the PRESET IDENTITY is stamped on at the
  // end. Before this slice the key was consumed here and thrown away, so a "Dieses
  // Jahr" click and a hand-picked Januar–September became the same object — which
  // is why nothing downstream could tell a YTD comparison from a custom one
  // without inspecting the dates, and inspecting the dates is exactly the guess
  // this slice removes.
  const bounds = periodBoundsForPreset(key, now);
  // Only a REAL preset carries identity. An unrecognised key falls through to the
  // current month below, and a fallback is not an intent — it is stamped `null`
  // so it serialises as a plain month rather than as a preset nothing can parse.
  return { ...bounds, presetKey: isPeriodPresetKey(key) ? key : null };
}

/**
 * The `{mode, from, to}` bounds a preset names — unchanged from 6A.3b.
 *
 * Split out of `presetToPeriod` so the switch stays the only place that defines a
 * preset's dates while the identity is stamped in exactly one place above.
 */
function periodBoundsForPreset(key, now) {
  const { year: cy, month: cm } = currentBusinessPeriod(now);
  const cur = () => ({ year: cy, month: cm });
  // (cy, cm) minus N months, year-wrap via Date normalization (negative month rolls back).
  // NOT a clock read: `cy`/`cm` are already-resolved Swiss integers, and this Date is
  // both CONSTRUCTED and READ in the host zone, so the two conversions cancel exactly.
  const minus = (n) => {
    const d = new Date(cy, cm - 1 - n, 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  };
  switch (key) {
    case "current":
      return { mode: "month", from: cur(), to: cur() };
    case "prev": {
      const p = minus(1);
      return { mode: "month", from: { ...p }, to: { ...p } };
    }
    case "last3":
      return { mode: "custom", from: minus(2), to: cur() };
    case "last6":
      return { mode: "custom", from: minus(5), to: cur() };
    case "last12":
      return { mode: "custom", from: minus(11), to: cur() };
    case "ytd":
      return { mode: "custom", from: { year: cy, month: 1 }, to: cur() };
    case "prevYear":
      return { mode: "custom", from: { year: cy - 1, month: 1 }, to: { year: cy - 1, month: 12 } };
    case "all":
      return { mode: "all", from: null, to: cur() };
    default:
      return { mode: "month", from: cur(), to: cur() };
  }
}

/**
 * 6A.3b: human label for the picker trigger.
 *   all    → "Gesamte Zeit"
 *   month  → "Juni 2026"
 *   custom → same year: "April – Juni 2026"; diff year: "Juli 2025 – Juni 2026"
 */
export function periodTriggerLabel(period) {
  const monthYear = (y, m) =>
    new Date(y, m - 1, 1).toLocaleDateString("de-CH", { month: "long", year: "numeric" });
  const monthOnly = (y, m) =>
    new Date(y, m - 1, 1).toLocaleDateString("de-CH", { month: "long" });
  if (!period || period.mode === "all") return "Gesamte Zeit";
  const { from, to } = period;
  if (period.mode === "month" || from == null) return monthYear(to.year, to.month);
  if (from.year === to.year) {
    return `${monthOnly(from.year, from.month)} – ${monthYear(to.year, to.month)}`;
  }
  return `${monthYear(from.year, from.month)} – ${monthYear(to.year, to.month)}`;
}

export function monthEndDateString(year, month) {
  const lastDay = new Date(year, month, 0);
  const y = lastDay.getFullYear();
  const m = String(lastDay.getMonth() + 1).padStart(2, "0");
  const day = String(lastDay.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function occupancyMonthPoint(label, summary) {
  const occupied = Number(summary?.occupied_rooms ?? 0);
  const free = Number(summary?.free_rooms ?? 0);
  const reserved = Number(summary?.reserved_rooms ?? 0);
  // Historical occupancy: a future-move-in room (reserved) was unoccupied in past months → free.
  return { month: label, occupied, free: free + reserved };
}

/**
 * Primary label for a performance-ranking row. Prefers the backend-enriched
 * unit_display_label (e.g. "CL-003 · Dufourstrasse 131"); falls back to the
 * legacy unit_name, then a neutral placeholder. No units-state lookup — the
 * label is rendered straight from the ranking response.
 */
export function rankingUnitLabel(row) {
  return row?.unit_display_label || row?.unit_name || "Unbekannte Einheit";
}
